import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
    DELETE_AUTOMATION,
    INSERT_AUTOMATION,
    QUERY_AUTOMATIONS,
    QUERY_AUTOMATION_CLIENTS,
    UPDATE_AUTOMATION,
} from './graphql';
import { clientOptions } from './utils';

export const useAutomations = () => {
    const { data, loading, error } = useQuery(QUERY_AUTOMATIONS);
    const { data: clientsData, loading: clientsLoading } = useQuery(QUERY_AUTOMATION_CLIENTS);

    const [insertAutomation, { loading: inserting }] = useMutation(INSERT_AUTOMATION, {
        update: (cache, { data: result }) => {
            const created = result?.insert_automations_one;
            if (!created) return;

            cache.updateQuery({ query: QUERY_AUTOMATIONS }, (existing) => ({
                automations: [...(existing?.automations || []), created],
            }));
        },
    });
    const [updateAutomation, { loading: updating }] = useMutation(UPDATE_AUTOMATION);
    const [deleteAutomation, { loading: deleting }] = useMutation(DELETE_AUTOMATION, {
        update: (cache, { data: result }) => {
            const deleted = result?.delete_automations_by_pk;
            if (!deleted) return;

            cache.evict({ id: cache.identify({ __typename: 'automations', ...deleted }) });
            cache.gc();
        },
    });

    const clients = useMemo(() => clientOptions(clientsData?.clients), [clientsData]);

    const save = useCallback(
        (automationId, input) =>
            automationId
                ? updateAutomation({ variables: { automation_id: automationId, update: input } })
                : insertAutomation({ variables: { object: input } }),
        [insertAutomation, updateAutomation]
    );

    const remove = useCallback(
        (automationId) => deleteAutomation({ variables: { automation_id: automationId } }),
        [deleteAutomation]
    );

    return {
        automations: data?.automations || [],
        loading,
        error,
        clients,
        clientsLoading,
        saving: inserting || updating,
        deleting,
        save,
        remove,
    };
};
